import React, { useState, useRef, useMemo, useEffect } from 'react';


export const TabsExtended = ({
  children,
  defaultIndex = 0,
  onChange,
  className = "",
  tabBarExtra = null,
}) => {
  const tabs = React.Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(defaultIndex);

  const listRef = useRef(null);
  const btnRefs = useRef([]);

  // Ensure the refs array length matches the number of tabs
  btnRefs.current = useMemo(
    () => tabs.map((_, i) => btnRefs.current[i] ?? React.createRef()),
    [tabs.length]
  );

  const setIdx = (i) => {
    setActiveIndex(i);
    onChange?.(i);
  };

  // Keyboard navigation: Left/Right/Home/End
  const onKeyDown = (e) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;

    e.preventDefault();
    const last = tabs.length - 1;

    if (e.key === "ArrowRight") setIdx(activeIndex === last ? 0 : activeIndex + 1);
    if (e.key === "ArrowLeft") setIdx(activeIndex === 0 ? last : activeIndex - 1);
    if (e.key === "Home") setIdx(0);
    if (e.key === "End") setIdx(last);

    // Focus the new active button for a11y
    requestAnimationFrame(() => {
      btnRefs.current[activeIndex]?.current?.focus();
    });
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Tablist */}
      <div
        ref={listRef}
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="
          relative
          flex flex-wrap
          gap-0.5
          rounded-t-lg
          bg-white/50 dark:bg-white/5
          p-0.5
          backdrop-blur
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {tabs.map((tab, idx) => {
          const title = tab.props.title ?? `Tab ${idx + 1}`;
          const isActive = idx === activeIndex;
          const id = `tab-${idx}`;
          const panelId = `panel-${idx}`;

          return (
            <button
              key={id}
              ref={btnRefs.current[idx]}
              id={id}
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setIdx(idx)}
              className={[
                "relative",
                "px-2.5 py-1",
                "text-xs",
                "font-semibold",
                "rounded-md sm:rounded-xl",
                "transition-colors duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70",
                "whitespace-nowrap", // Prevent text wrapping within tab labels
                isActive
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
                isActive
                  ? "bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/5"
                  : "bg-white/40 hover:bg-white/60 dark:bg-white/0 dark:hover:bg-white/5",
              ].join(" ")}
            >
              {/* Subtle glow when active */}
              {isActive && (
                <span
                  className="
                    pointer-events-none absolute inset-0 -z-10
                    rounded-lg sm:rounded-xl
                    shadow-[0_0_16px_-4px_rgba(56,189,248,0.35)]
                    sm:shadow-[0_0_24px_-6px_rgba(56,189,248,0.35)]
                    dark:shadow-[0_0_16px_-4px_rgba(129,140,248,0.4)]
                    dark:sm:shadow-[0_0_24px_-6px_rgba(129,140,248,0.4)]
                  "
                />
              )}
              {title}
            </button>
          );
        })}
        {tabBarExtra && (
          <div className="ml-auto flex items-center">
            {tabBarExtra}
          </div>
        )}
      </div>

      {/* Panels */}
      <div
        className="
          rounded-b-lg
          bg-white/60 dark:bg-white/[0.03]
          p-2
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {tabs.map((tab, idx) => {
          const isActive = idx === activeIndex;
          const id = `panel-${idx}`;
          const tabId = `tab-${idx}`;
          return (
            <div
              key={id}
              id={id}
              role="tabpanel"
              aria-labelledby={tabId}
              hidden={!isActive}
              className="animate-fadeIn"
            >
              {isActive && tab}
            </div>
          );
        })}
      </div>
    </div>
  );
};